import type {
  FilterParams,
  SearchParams,
} from '@/modules/common/context/QueryParams/models';
import type {TrackingEvent as GenericTrackingEvent} from '@/redux/middleware/googleTagManager/types';
import type {Page} from '@/types';

import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {
  bd4RecommendedClicked,
  bd4RecommendedLoaded,
  bestOfferBoxesLoadedEvent,
  pageLoadEvent,
  pageViewEvent,
} from '@/redux/middleware/googleTagManager';

let didTrackPageLoad = false;

export const EVENT_BEST_OFFER_BOXES_LOADED = 'bestOfferBoxesLoaded';
export const EVENT_BD4_RECOMMENDED_LOADED = 'bd4RecommendedLoaded';
export const EVENT_BD4_RECOMMENDED_CLICKED = 'bd4RecommendedClicked';

const eventsMap = {
  [EVENT_BEST_OFFER_BOXES_LOADED]: bestOfferBoxesLoadedEvent,
  [EVENT_BD4_RECOMMENDED_LOADED]: bd4RecommendedLoaded,
  [EVENT_BD4_RECOMMENDED_CLICKED]: bd4RecommendedClicked,
};

type TrackingEvent = keyof typeof eventsMap;

type TrackPageImpression = (params: {
  page: Page;
  searchParams: SearchParams;
  filterParams: FilterParams;
  defaultSearchParams: SearchParams;
}) => void;

type TrackEvent = (name: TrackingEvent, params?: any) => void;

export const useTracking = () => {
  const dispatch = useDispatch();

  const trackPageImpression: TrackPageImpression = useCallback(
    ({page, searchParams, filterParams, defaultSearchParams}) => {
      // The very first impression is considered a "pageLoad" and should only be tracked once *per session*.
      // Any on page navigation afterwards is a "pageView".
      if (didTrackPageLoad) {
        dispatch(pageViewEvent.action({page, searchParams}));
      } else {
        dispatch(
          pageLoadEvent.action({
            page,
            defaultSearchParams,
            searchParams,
            filterParams,
          })
        );
        didTrackPageLoad = true;
      }
    },

    [dispatch]
  );

  const trackEvent: TrackEvent = useCallback(
    (name, params) => {
      if (typeof eventsMap[name]?.action === 'function') {
        dispatch(eventsMap[name].action(params));
      }
    },
    [dispatch]
  );

  const dispatchGenericTrackingEvent = useCallback(
    (payload: GenericTrackingEvent['payload']) => {
      dispatch<GenericTrackingEvent>({
        type: 'TRACKING',
        payload,
      });
    },
    [dispatch]
  );

  return {
    trackPageImpression,
    trackEvent,
    dispatchGenericTrackingEvent,
  };
};
